class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById("cart-notification");
    this.backdrop = document.getElementById("cart-notification-backdrop");
    this.header = document.querySelector("sticky-header");

    // Escape and the popup's own buttons hand focus back to the add button (keyboard only, see close()).
    // A key event with no code (synthetic or assistive input) falls back to its key.
    this.notification.addEventListener(
      "keyup",
      (evt) => (evt.code || evt.key) === "Escape" && this.close({ restoreFocus: true }),
    );

    this.querySelectorAll('button[type="button"]').forEach((btn) =>
      btn.addEventListener("click", () => this.close({ restoreFocus: true })),
    );

    this.querySelectorAll("a[href]").forEach((link) =>
      link.addEventListener("click", this.close.bind(this)),
    );

    window.addEventListener("pageshow", (e) => {
      if (e.persisted) this.close();
    });

    if (this.backdrop) {
      this.backdrop.addEventListener("click", this.close.bind(this));
    }

    const cartIconLink = document.getElementById("cart-icon-bubble");
    if (cartIconLink) {
      cartIconLink.addEventListener("click", () => {
        if (this.classList.contains("active")) this.close();
      });
    }

    this._hovered = false;
    this.notification.addEventListener("pointerenter", (e) => {
      if (e.pointerType === "mouse") this._hovered = true;
    });
    this.notification.addEventListener("pointerleave", (e) => {
      if (e.pointerType === "mouse") this._hovered = false;
    });

    // A tap inside the popup, or focus moved onto one of its links or buttons (Tab, or a screen reader moving
    // through it), means the shopper is using it: it then stays open until they close it.
    this.notification.addEventListener("pointerdown", (e) => {
      if (e.pointerType !== "mouse") this._cancelAutoClose();
    });
    this.notification.addEventListener("focusin", (e) => {
      if (e.target !== this.notification) this._cancelAutoClose();
    });
  }

  _isHovered() {
    return this._hovered;
  }

  // Someone on one of the popup's controls keeps it open, like a hovering mouse does.
  _hasFocusInside() {
    const active = document.activeElement;
    return active !== this.notification && this.notification.contains(active);
  }

  _cancelAutoClose() {
    if (this._autoCloseTimer) clearTimeout(this._autoCloseTimer);
    this._autoCloseTimer = null;
  }

  _scheduleAutoClose(ms) {
    if (this._autoCloseTimer) clearTimeout(this._autoCloseTimer);
    this._autoCloseTimer = setTimeout(() => {
      this._autoCloseTimer = null;
      if (this._isHovered() || this._hasFocusInside()) {
        this._scheduleAutoClose(1000);
        return;
      }
      this.close({ restoreFocus: true });
    }, ms);
  }

  open(opener) {
    if (this._autoCloseTimer) clearTimeout(this._autoCloseTimer);

    const candidate = opener || document.activeElement;
    if (candidate && candidate !== document.body && !this.notification.contains(candidate)) {
      this._opener = candidate;
    }

    // Focus moves in once the open transition ends. When the popup is already open (a second add) no
    // transition runs, so focus moves now; a listener left waiting would fire on the close transition and
    // call trapFocus, wiping the trap of whatever overlay opened meanwhile.
    if (this._pendingFocus) this.notification.removeEventListener("transitionend", this._pendingFocus);
    const focusIn = () => {
      this._pendingFocus = null;
      this.notification.focus();
      trapFocus(this.notification);
    };
    const wasActive = this.classList.contains("active");

    this.classList.add("active");

    if (wasActive) {
      focusIn();
    } else {
      this._pendingFocus = focusIn;
      this.notification.addEventListener("transitionend", focusIn, { once: true });
    }

    // Only an add made with a tap or a click closes the popup by itself, after 10 s (it used to be 5 s, too short
    // to hear it read). Opened from the keyboard, or with no tap or click seen (a screen reader's own commands),
    // the popup holds focus and stays until it is closed, so its reading is never cut off.
    if (document.documentElement.getAttribute("data-hb-input") === "pointer") this._scheduleAutoClose(10000);
    else this._cancelAutoClose();
  }

  close(options) {
    const restoreFocus = Boolean(options && options.restoreFocus === true);
    if (this._autoCloseTimer) {
      clearTimeout(this._autoCloseTimer);
      this._autoCloseTimer = null;
    }
    if (this._pendingFocus) {
      this.notification.removeEventListener("transitionend", this._pendingFocus);
      this._pendingFocus = null;
    }

    this.classList.remove("active");
    this._hovered = false;

    const activeElement = document.activeElement;
    const opener = this._opener;
    this._opener = null;
    removeTrapFocus();
    if (activeElement && this.notification.contains(activeElement)) {
      // Keyboard and screen-reader users go back to the control that added the item. After a tap or a
      // click focus is dropped as before (a focused card would stop a carousel's autoplay, for one).
      if (
        restoreFocus &&
        opener &&
        opener.isConnected &&
        document.documentElement.getAttribute("data-hb-input") !== "pointer"
      ) {
        opener.focus({ preventScroll: true });
      }
      if (this.notification.contains(document.activeElement)) activeElement.blur();
    }
  }

  renderContents(parsedState, options) {
    this.cartItemKey =
      parsedState.key ||
      (parsedState.items && parsedState.items.length && parsedState.items[0].key) ||
      undefined;
    this.cartItemVariantId =
      parsedState.variant_id ||
      (parsedState.items && parsedState.items.length && parsedState.items[0].variant_id) ||
      undefined;

    // window.hbPageType is set by the layout; the pathname test remains for pages that render it late.
    const isCartPage = window.hbPageType ? window.hbPageType === "cart" : window.location.pathname.includes("/cart");

    if (isCartPage) {
      this.clearCartPageEmptyState();
    }

    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      if (!el) return;
      const sectionKey = section.section || section.id;
      const html = parsedState.sections && parsedState.sections[sectionKey];
      if (!html) return;

      if (isCartPage) {
        const target = (section.selector && el.querySelector(section.selector)) || el;
        target.innerHTML = this.getSectionInnerHTML(html, section.selector, section.fallbackSelector);
      } else {
        el.innerHTML = this.getSectionInnerHTML(html, section.selector, section.fallbackSelector);
      }
    });

    if (isCartPage) return;

    // Allow the PDP inline quantity selector to override the notification heading
    if (typeof window.__pdpInlineHeadingOverride === "string" && window.__pdpInlineHeadingOverride) {
      const headingEl = this.notification?.querySelector(".cart-notification__heading");
      if (headingEl) headingEl.textContent = window.__pdpInlineHeadingOverride;
    }
    window.__pdpInlineHeadingOverride = null;

    if (this.header) this.header.reveal();
    this.open(options && options.opener);
  }

  clearCartPageEmptyState() {
    document.querySelector("cart-items.cart-page")?.classList.remove("is-empty");
    document.getElementById("main-cart-footer")?.classList.remove("is-empty");
    document.getElementById("cart-page-milestone-progress")?.classList.remove("is-empty");
  }

  getSectionsToRender() {
    const isCartPage = window.hbPageType ? window.hbPageType === "cart" : window.location.pathname.includes("/cart");

    if (isCartPage) {
      return [
        {
          id: "cart-page-milestone-progress",
          section: document.getElementById("cart-page-milestone-progress")?.dataset.id,
          selector: ".progress-section",
        },
        {
          id: "cart-samples-gifts-tabs",
          section: document.getElementById("cart-samples-gifts-tabs")?.dataset.id,
          selector: ".js-contents",
        },
        {
          id: "main-cart-items",
          section: document.getElementById("main-cart-items")?.dataset.id,
          selector: ".js-contents",
        },
        {
          id: "main-cart-footer",
          section: document.getElementById("main-cart-footer")?.dataset.id,
          selector: ".cart__footer",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
        },
      ].filter((s) => s.section);
    }

    return [
      {
        id: "cart-notification-product",
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
        fallbackSelector: this.cartItemVariantId
          ? `[data-cart-notification-variant-id="${this.cartItemVariantId}"]`
          : ".cart-item",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section", fallbackSelector) {
    const parsedHtml = new DOMParser().parseFromString(html, "text/html");
    const node =
      parsedHtml.querySelector(selector) ||
      (fallbackSelector && parsedHtml.querySelector(fallbackSelector));
    if (!node) return "";
    // sections/cart-notification-product.liquid prints the progress bar once after the lines; it goes back
    // where every line used to carry its own copy, as the last child of the line's details.
    const progress = parsedHtml.querySelector(
      "[data-cart-notification-progress] > .cart-notification__progress-wrapper",
    );
    const details = progress && node.querySelector(".cart-notification-product__details");
    if (details) details.appendChild(progress);
    return node.innerHTML;
  }
}

customElements.define("cart-notification", CartNotification);
